/**
 * HttpContentTransport — binary I/O over HTTP.
 *
 * Phase 1 of TRANSPORT-ABSTRACTION. Narrow by design because binary has
 * different backpressure and streaming characteristics than typed command
 * payloads. Uses the HttpTransport's underlying ky instance + token, so
 * retries, logging, and auth behave identically to the rest of the wire.
 *
 * Two `putBinary` paths live side by side, selected by runtime
 * environment + caller intent:
 *   - **ky path (default + Node)** — the original `ky.post(...)` path.
 *     Keeps retry-with-refresh, beforeError → APIError, observability
 *     spans intact. Hits when no `onProgress`/`signal` is passed, OR
 *     when `XMLHttpRequest` isn't available in the runtime (Node
 *     workers, the CLI). On Node-side `signal`-aborts: the in-flight
 *     `fetch` continues in the background and the `cancelled` flag in
 *     `yield.resource` suppresses the resolve/reject callbacks.
 *   - **XHR path (browsers with `onProgress` or `signal`)** — hand-rolled
 *     because `ky` wraps `fetch` which can't observe upload byte-
 *     progress today (`Request({ duplex: 'half' })` is the long-term
 *     direction; not yet widely available across the webviews this
 *     codepath needs to run in). Threads auth + traceparent headers,
 *     emits `onProgress` from `xhr.upload.onprogress`, supports
 *     cancellation via the `signal` option (calling `xhr.abort()`),
 *     and routes failures onto the same `transport.errors$` stream
 *     the ky path uses.
 *
 * The runtime check on `XMLHttpRequest` is the load-bearing seam: a
 * Node worker calling `client.yield.resource(...)` (which always passes
 * a `signal` for unsubscribe-aborts) must NOT take the XHR path —
 * `XMLHttpRequest` is undefined and the upload throws synchronously.
 * Browsers always have it; Node does not.
 *
 * v1 limitation: the XHR path does NOT auto-refresh on 401. Mitigation:
 * the session's proactive refresh fires before token expiry, so an
 * upload that *starts* with a fresh token usually completes. An upload
 * spanning the narrow window between expiry and proactive-refresh would
 * fail; the existing `errors$` → modal path surfaces it as session-
 * expired. If retry-with-refresh on the upload path becomes a real
 * complaint, wire a manual retry loop here that reads `token$` afresh.
 */

import type { AccessToken, ExtractionOutcome, ResourceId, PutBinaryOptions, components } from '@semiont/core';
import { busLog } from '@semiont/core';
import { SpanKind, getActiveTraceparent, withSpan } from '@semiont/observability';
import type { HttpTransport } from './http-transport';
import { APIError } from './http-transport';
import type { IContentTransport, PutBinaryRequest } from '@semiont/core';

type GetResourceResponse = components['schemas']['GetResourceResponse'];

export class HttpContentTransport implements IContentTransport {
  constructor(private readonly transport: HttpTransport) {}

  async putBinary(
    request: PutBinaryRequest,
    options?: PutBinaryOptions,
  ): Promise<{ resourceId: ResourceId }> {
    const sizeBytes = request.file instanceof File ? request.file.size : request.file.length;
    busLog('PUT', 'content', {
      name: request.name,
      format: request.format,
      storageUri: request.storageUri,
      sizeBytes,
    });
    return withSpan(
      'content.put',
      async () => {
        const formData = buildFormData(request);
        const headers = this.requestHeaders(options?.auth);

        // Branch on caller intent AND runtime support. The ky path is
        // the well-trodden default; the XHR path lights up only when a
        // caller wants byte progress or cancellation AND the runtime
        // has `XMLHttpRequest` (browsers do; Node does not). Without
        // the runtime guard, every Node-side `yield.resource(...)`
        // call (which always passes `signal`) would throw
        // `XMLHttpRequest is not defined`.
        const xhrAvailable = typeof XMLHttpRequest !== 'undefined';
        if (xhrAvailable && (options?.onProgress || options?.signal)) {
          return uploadViaXhr({
            url: `${this.transport.baseUrl}/resources`,
            formData,
            headers,
            onProgress: options.onProgress,
            signal: options.signal,
            onApiError: (err) => this.transport.pushError(err),
          });
        }

        const result = await this.transport.rawHttp
          .post(`${this.transport.baseUrl}/resources`, {
            body: formData,
            headers,
          })
          .json<{ resourceId: string }>();

        return { resourceId: result.resourceId as ResourceId };
      },
      {
        kind: SpanKind.CLIENT,
        attrs: {
          'content.format': request.format,
          'content.size_bytes': sizeBytes,
        },
      },
    );
  }

  async getBinary(
    resourceId: ResourceId,
    options?: { auth?: AccessToken },
  ): Promise<{ data: ArrayBuffer; contentType: string }> {
    busLog('GET', 'content', { resourceId });
    return withSpan(
      'content.get',
      async () => {
        // Pure pipe: no Accept header — the route serves the stored bytes
        // verbatim with their real Content-Type (SIMPLER-JSON-LD.md).
        const response = await this.transport.rawHttp.get(`${this.transport.baseUrl}/resources/${resourceId}`, {
          headers: this.requestHeaders(options?.auth),
        });
        const contentType = response.headers.get('content-type') || 'application/octet-stream';
        const data = await response.arrayBuffer();
        return { data, contentType };
      },
      { kind: SpanKind.CLIENT, attrs: { 'resource.id': resourceId as unknown as string } },
    );
  }

  async getBinaryStream(
    resourceId: ResourceId,
    options?: { auth?: AccessToken },
  ): Promise<{ stream: ReadableStream<Uint8Array>; contentType: string }> {
    busLog('GET', 'content', { resourceId, stream: true });
    return withSpan(
      'content.get',
      async () => {
        // Pure pipe: no Accept header (see getBinary).
        const response = await this.transport.rawHttp.get(`${this.transport.baseUrl}/resources/${resourceId}`, {
          headers: this.requestHeaders(options?.auth),
        });
        const contentType = response.headers.get('content-type') || 'application/octet-stream';
        if (!response.body) {
          throw new Error('Response body is null - cannot create stream');
        }
        return { stream: response.body, contentType };
      },
      {
        kind: SpanKind.CLIENT,
        attrs: { 'resource.id': resourceId as unknown as string, 'content.stream': true },
      },
    );
  }

  /**
   * Dereference the resource's JSON-LD graph over HTTP — the LD face an
   * external linked-data client sees. Deliberately HTTP, not the bus
   * (SIMPLER-JSON-LD.md §5).
   */
  async getResourceGraph(
    resourceId: ResourceId,
    options?: { auth?: AccessToken },
  ): Promise<GetResourceResponse> {
    busLog('GET', 'content', { resourceId, graph: true });
    return withSpan(
      'content.get_graph',
      () =>
        this.transport.rawHttp
          .get(`${this.transport.baseUrl}/resources/${resourceId}/jsonld`, {
            headers: this.requestHeaders(options?.auth),
          })
          .json<GetResourceResponse>(),
      { kind: SpanKind.CLIENT, attrs: { 'resource.id': resourceId as unknown as string, 'content.graph': true } },
    );
  }

  /**
   * Store a resource's derived coordinate map (ANCHORED-TEXT-CACHE Lane 5).
   *
   * The Smelter is the producer and runs as its own process, which is why this
   * crosses the wire at all: the map goes to the one store the KnowledgeSystem
   * owns, rather than to a volume shared between service images.
   */
  async putAnchoredText(
    checksum: string,
    outcome: ExtractionOutcome,
    options?: { auth?: AccessToken },
  ): Promise<void> {
    busLog('PUT', 'anchored-text', { checksum });
    await withSpan(
      'content.put_anchored_text',
      () =>
        this.transport.rawHttp
          .put(`${this.transport.baseUrl}/anchored-text/${checksum}`, {
            headers: this.requestHeaders(options?.auth),
            json: outcome,
          })
          .json<unknown>(),
      { kind: SpanKind.CLIENT, attrs: { 'content.checksum': checksum } },
    );
  }

  /**
   * The resource's coordinate map, or `null` when none has been derived —
   * which is the common case and not an error: callers degrade to no quoted
   * text.
   *
   * 204 is that answer, and the body is empty, so it must be taken before
   * `.json()` is reached — parsing an empty body throws, which would turn the
   * ordinary case into a failure. A 404 degrades the same way, though it is a
   * different fact: the resource itself is absent, and a resource that does
   * not exist has no map either.
   */
  async getAnchoredText(
    resourceId: ResourceId,
    options?: { auth?: AccessToken },
  ): Promise<ExtractionOutcome | null> {
    busLog('GET', 'anchored-text', { resourceId });
    return withSpan(
      'content.get_anchored_text',
      async () => {
        const response = await this.transport.rawHttp
          .get(`${this.transport.baseUrl}/resources/${resourceId}/anchored-text`, {
            headers: this.requestHeaders(options?.auth),
            throwHttpErrors: false,
          });
        if (response.status === 204 || response.status === 404) return null;
        if (!response.ok) throw new Error(`anchored-text read failed: ${response.status}`);
        return response.json<ExtractionOutcome>();
      },
      { kind: SpanKind.CLIENT, attrs: { 'resource.id': resourceId as unknown as string } },
    );
  }

  /**
   * The cache-consult read (PERSIST-ANCHORS P2c) — checksum-addressed and
   * barrier-free; 204 is the ordinary miss. This is how an out-of-process
   * extraction seam hits the cache at all.
   */
  async getAnchoredTextByChecksum(
    checksum: string,
    options?: { auth?: AccessToken },
  ): Promise<ExtractionOutcome | null> {
    busLog('GET', 'anchored-text-by-checksum', { checksum });
    return withSpan(
      'content.get_anchored_text_by_checksum',
      async () => {
        const response = await this.transport.rawHttp
          .get(`${this.transport.baseUrl}/anchored-text/${checksum}`, {
            headers: this.requestHeaders(options?.auth),
            throwHttpErrors: false,
          });
        if (response.status === 204) return null;
        if (!response.ok) throw new Error(`anchored-text checksum read failed: ${response.status}`);
        return response.json<ExtractionOutcome>();
      },
      { kind: SpanKind.CLIENT, attrs: { 'content.checksum': checksum } },
    );
  }

  /**
   * The store's would-hit keys — the reconcile planner's bulk existence read
   * (PERSIST-ANCHORS P0). One request per reconcile; keys only, never the
   * maps themselves, which is the point of the dedicated route.
   */
  async listAnchoredTextKeys(options?: { auth?: AccessToken }): Promise<string[]> {
    busLog('GET', 'anchored-text-keys', {});
    return withSpan(
      'content.list_anchored_text_keys',
      async () => {
        const { keys } = await this.transport.rawHttp
          .get(`${this.transport.baseUrl}/anchored-text/keys`, {
            headers: this.requestHeaders(options?.auth),
          })
          .json<{ keys: string[] }>();
        return keys;
      },
      { kind: SpanKind.CLIENT },
    );
  }

  dispose(): void {
    // HttpContentTransport has no resources of its own; HttpTransport owns
    // the ky instance and token subject. No-op is correct here.
  }

  /** Auth header + W3C trace propagation for the active span. */
  private requestHeaders(override?: AccessToken): Record<string, string> {
    const token = override ?? this.transport.getToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const trace = getActiveTraceparent();
    if (trace) {
      headers['traceparent'] = trace.traceparent;
      if (trace.tracestate) headers['tracestate'] = trace.tracestate;
    }
    return headers;
  }
}

function buildFormData(request: PutBinaryRequest): FormData {
  const formData = new FormData();
  formData.append('name', request.name);
  formData.append('format', request.format);
  formData.append('storageUri', request.storageUri);

  if (request.file instanceof File) {
    formData.append('file', request.file);
  } else if (typeof Buffer !== 'undefined' && Buffer.isBuffer(request.file)) {
    // `Buffer` is a Node global; referencing it bare in the browser throws
    // ReferenceError before the isBuffer call. Browser uploads always hit
    // the File branch above; this branch is for Node-side workers.
    const blob = new Blob([new Uint8Array(request.file)], { type: request.format });
    formData.append('file', blob, request.name);
  } else {
    throw new Error('file must be a File or Buffer');
  }

  if (request.entityTypes && request.entityTypes.length > 0) {
    formData.append('entityTypes', JSON.stringify(request.entityTypes));
  }
  if (request.language) formData.append('language', request.language);
  if (request.sourceAnnotationId) formData.append('sourceAnnotationId', String(request.sourceAnnotationId));
  if (request.sourceResourceId) formData.append('sourceResourceId', String(request.sourceResourceId));
  if (request.generationPrompt) formData.append('generationPrompt', request.generationPrompt);
  if (request.generator) formData.append('generator', JSON.stringify(request.generator));
  if (request.isDraft !== undefined) formData.append('isDraft', String(request.isDraft));

  return formData;
}

interface XhrUploadOptions {
  url: string;
  formData: FormData;
  headers: Record<string, string>;
  onProgress?: (event: { bytesUploaded: number; totalBytes: number }) => void;
  signal?: AbortSignal;
  onApiError: (error: APIError) => void;
}

/**
 * XHR-based POST that exposes `xhr.upload.onprogress` byte counts and
 * supports cancellation via `AbortSignal`. Mirrors the ky path's error
 * shape: 4xx/5xx and network-level failures both surface as `APIError`,
 * and every error is routed onto `transport.errors$` before the promise
 * rejects.
 */
function uploadViaXhr(opts: XhrUploadOptions): Promise<{ resourceId: ResourceId }> {
  const { url, formData, headers, onProgress, signal, onApiError } = opts;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (signal?.aborted) {
      const err = new APIError('Upload aborted', 0, 'aborted');
      onApiError(err);
      reject(err);
      return;
    }

    xhr.open('POST', url);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    if (onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent) => {
        // `lengthComputable` is true when Content-Length is known. For
        // FormData posts the browser computes it, so this is true in
        // practice; the false branch handles the rare chunked-encoding
        // / gzip-while-uploading case.
        const totalBytes = e.lengthComputable ? e.total : 0;
        onProgress({ bytesUploaded: e.loaded, totalBytes });
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { resourceId: string };
          resolve({ resourceId: body.resourceId as ResourceId });
        } catch (parseErr) {
          const err = new APIError(
            `Upload succeeded but response was not valid JSON: ${(parseErr as Error).message}`,
            xhr.status,
            xhr.statusText,
            xhr.responseText,
          );
          onApiError(err);
          reject(err);
        }
        return;
      }
      let body: unknown = xhr.responseText;
      try { body = JSON.parse(xhr.responseText); } catch { /* keep as text */ }
      const message = (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string')
        ? (body as { message: string }).message
        : `HTTP ${xhr.status}: ${xhr.statusText}`;
      const err = new APIError(message, xhr.status, xhr.statusText, body);
      onApiError(err);
      reject(err);
    };

    xhr.onerror = () => {
      // Network-level failure (DNS, TCP reset, CORS). XHR doesn't give
      // us a useful status here; classify as `unavailable` via 0 status
      // mapping in classifyApiCode.
      const err = new APIError('Network error during upload', 0, 'network-error');
      onApiError(err);
      reject(err);
    };

    xhr.ontimeout = () => {
      const err = new APIError('Upload timed out', 0, 'timeout');
      onApiError(err);
      reject(err);
    };

    xhr.onabort = () => {
      // Caller-initiated abort via `signal`. Emit a single APIError so the
      // shape matches the other failure paths; consumers can disambiguate
      // via `signal.aborted` if they need to.
      const err = new APIError('Upload aborted', 0, 'aborted');
      onApiError(err);
      reject(err);
    };

    if (signal) {
      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort, { once: true });
      // No teardown for the listener — once xhr fires onabort/onerror/onload
      // the signal is no longer relevant; the listener is GC'd with the xhr.
    }

    xhr.send(formData);
  });
}
